import {
  emptyResearchState,
  emptyTowerRanks,
  isResearchBranchId,
  isResearchTowerId,
  RESEARCH_BASE_MAX_RANK,
  RESEARCH_MAX_RANK,
  type ResearchBranchId,
  type ResearchState,
  type ResearchTowerId,
  type TowerResearchRanks
} from './types'
import { isAdvancedRank, researchCostForNextRank } from './costs'

export type PurchaseResearchFailureReason =
  | 'UNKNOWN_TOWER'
  | 'UNKNOWN_BRANCH'
  | 'MAX_RANK'
  | 'ADVANCED_LOCKED'
  | 'INSUFFICIENT_INGOTS'
  | 'INVALID_COST'

export interface PurchaseResearchInput {
  state: ResearchState
  ingots: number
  towerId: string
  branchId: string
  /**
   * When false, ranks beyond base max are blocked (Advanced Research).
   * Default true for harness / tests; UI passes chapter-2 unlock.
   */
  advancedUnlocked?: boolean
}

export interface PurchaseResearchSuccess {
  ok: true
  towerId: ResearchTowerId
  branchId: ResearchBranchId
  previousRank: number
  newRank: number
  cost: number
  ingotsAfter: number
  stateAfter: ResearchState
}

export interface PurchaseResearchFailure {
  ok: false
  reason: PurchaseResearchFailureReason
}

export type PurchaseResearchResult = PurchaseResearchSuccess | PurchaseResearchFailure

function clampRank(branch: ResearchBranchId, value: unknown): number {
  const max = RESEARCH_MAX_RANK[branch]
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : 0
  return Math.max(0, Math.min(max, n))
}

function sanitizeTowerRanks(raw: unknown): TowerResearchRanks {
  if (!raw || typeof raw !== 'object') return emptyTowerRanks()
  const r = raw as Partial<TowerResearchRanks>
  return {
    power: clampRank('power', r.power),
    range: clampRank('range', r.range),
    tempo: clampRank('tempo', r.tempo)
  }
}

/** Safe recovery from corrupt / partial / future fields. */
export function sanitizeResearchState(raw: unknown): ResearchState {
  const base = emptyResearchState()
  if (!raw || typeof raw !== 'object') return base
  const record = raw as { version?: unknown; towers?: unknown }
  if (record.version !== 1 && record.version !== undefined) {
    // Unknown future version — keep empty rather than corrupt ranks.
    if (typeof record.version === 'number' && record.version > 1) {
      // Best-effort: still try to read known towers if present.
    } else if (record.version !== 1) {
      return base
    }
  }
  if (!record.towers || typeof record.towers !== 'object') return base
  const towers = record.towers as Record<string, unknown>
  for (const id of Object.keys(base.towers) as ResearchTowerId[]) {
    base.towers[id] = sanitizeTowerRanks(towers[id])
  }
  return base
}

/**
 * Pure purchase transaction. Does not touch storage.
 * Callers must persist stateAfter + ingotsAfter atomically at the application layer.
 */
export function purchaseResearchRank(input: PurchaseResearchInput): PurchaseResearchResult {
  if (!isResearchTowerId(input.towerId)) {
    return { ok: false, reason: 'UNKNOWN_TOWER' }
  }
  if (!isResearchBranchId(input.branchId)) {
    return { ok: false, reason: 'UNKNOWN_BRANCH' }
  }
  const towerId = input.towerId
  const branchId = input.branchId
  const ranks = input.state.towers[towerId] ?? emptyTowerRanks()
  const previousRank = ranks[branchId]
  const max = RESEARCH_MAX_RANK[branchId]
  if (previousRank >= max) {
    return { ok: false, reason: 'MAX_RANK' }
  }
  const nextRank = previousRank + 1
  const advancedUnlocked = input.advancedUnlocked !== false
  if (isAdvancedRank(branchId, nextRank) && !advancedUnlocked) {
    return { ok: false, reason: 'ADVANCED_LOCKED' }
  }
  // Soft guard: never skip past base without unlock even if somehow requested.
  if (!advancedUnlocked && previousRank >= RESEARCH_BASE_MAX_RANK[branchId]) {
    return { ok: false, reason: 'ADVANCED_LOCKED' }
  }
  const cost = researchCostForNextRank(branchId, previousRank)
  if (cost == null || cost <= 0) {
    return { ok: false, reason: 'INVALID_COST' }
  }
  const ingots = Math.max(0, Math.floor(input.ingots))
  if (ingots < cost) {
    return { ok: false, reason: 'INSUFFICIENT_INGOTS' }
  }

  const newRank = previousRank + 1
  const nextRanks: TowerResearchRanks = { ...ranks, [branchId]: newRank }
  const stateAfter: ResearchState = {
    version: 1,
    towers: {
      ...input.state.towers,
      [towerId]: nextRanks
    }
  }

  return {
    ok: true,
    towerId,
    branchId,
    previousRank,
    newRank,
    cost,
    ingotsAfter: ingots - cost,
    stateAfter
  }
}
